import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api, ApiError,
  type CardTexts, type Cue, type CueSet, type Transcript,
  type VideoAspect, type VideoCardSize, type VideoRecord,
} from '../api/client.ts';
import { useCancelledOnUnmount } from '../lib/use-cancelled.ts';
import { CueEditor } from './CueEditor.tsx';
import { t } from '../i18n/index.ts';
import { formatDuration, formatBytes } from '../lib/format.ts';
import { giveUpAfterMs } from '../lib/wait.ts';

const POLL_MS = 1500;
/** Only used until the server says how long it expects to take. */
const GIVE_UP_FLOOR_MS = 15 * 60 * 1000;
/**
 * A render whose progress has not moved for this long is treated as lost. It
 * has to be longer than the slowest single step a render takes without
 * reporting -- on the server the encode of a long clip is the worst of them.
 */
const STALLED_MS = 5 * 60 * 1000;

/**
 * How long a render will wait for its turn before the page gives up on it.
 *
 * Separate from STALLED_MS, and much longer, because the two are not the same
 * situation and treating them alike was wrong. One worker runs one job at a
 * time, so when two people render at once the second sits at 'queued' and 0%
 * for as long as the first one takes. Nothing is broken; nothing is stalled;
 * there is simply nothing to report yet. Measured against the stall clock that
 * looked identical to a wedged render, and the second person's page abandoned a
 * perfectly healthy job -- silently, because a queued row matches neither the
 * failed branch nor the ready one.
 *
 * Thirty minutes covers the longest clip the app accepts (five minutes of
 * audio, about eighteen minutes to render here) with one ahead of it.
 */
const QUEUE_PATIENCE_MS = 30 * 60 * 1000;

/**
 * How the wait after choosing a file divides up.
 *
 * Sending the bytes is measurable exactly; what happens afterwards is not, so
 * the two are weighted rather than treated alike. On a control-room network the
 * upload is the quick half, which is why it takes the smaller share -- a bar
 * that races to full and then sits there is the fault it exists to prevent.
 */
const UPLOAD_SHARE = 35;

/**
 * What the transcription half is estimated against.
 *
 * The server says, because only the server knows. Whisper reports its own
 * progress, but with voice activity detection enabled it is wrong -- observed
 * printing 86% and then 172% while still working -- so the wait has to be
 * modelled rather than read. It was modelled here first, at ten times real
 * speed taken from a laptop, and the production server turned out to decode at
 * a third of real time: thirty times slower than the page believed. The bar
 * reached its ceiling in seconds and then sat on 97% for minutes on work that
 * was going perfectly well, and the desk reported the tool as broken.
 *
 * So `expected_sec` comes from the median of what that server has actually
 * done, and this is only the fallback for a server with no history yet.
 */
const TRANSCRIBE_OVERHEAD_MS = 20_000;
const TRANSCRIBE_RATE = 0.3;

/** The estimate stops here. Only the server saying so moves it to 100. */
const ESTIMATE_CEILING = 97;

// Keys written out rather than built, so the typed `t()` checks each one
// against ar.json at build time.
const ASPECTS = [
  { value: 'vertical' as VideoAspect, label: 'video.vertical' as const,
    note: 'video.vertical_note' as const },
  { value: 'square' as VideoAspect, label: 'video.square' as const,
    note: 'video.square_note' as const },
];

/**
 * How large the card is drawn in the frame.
 *
 * The swatch is the point of this control: the card and the frame it sits in,
 * to scale, so the choice reads as how much of the screen the words take rather
 * than as two words that both mean "size".
 */
const CARD_SIZES = [
  { value: 'default' as VideoCardSize, label: 'video.size_default' as const,
    note: 'video.size_default_note' as const },
  { value: 'small' as VideoCardSize, label: 'video.size_small' as const,
    note: 'video.size_small_note' as const },
];

/**
 * Blank means "work it out": the station line and the timestamp are filled in
 * at render time from the clip itself, and only a value typed here overrides
 * them.
 */
function blankTexts(): CardTexts {
  return {
    guestName: t('video.guestName_default'),
    title: '',
    station: '',
    stamp: '',
    footer: t('video.default_footer'),
  };
}

const TEXT_FIELDS = [
  { key: 'guestName' as const, label: 'video.guestName' as const,
    hint: 'video.guestName_hint' as const },
  { key: 'title' as const, label: 'video.text_title' as const,
    hint: 'video.text_title_hint' as const },
  { key: 'station' as const, label: 'video.text_station' as const,
    hint: 'video.text_blank' as const },
  { key: 'stamp' as const, label: 'video.text_stamp' as const,
    hint: 'video.text_blank' as const },
  { key: 'footer' as const, label: 'video.text_footer' as const,
    hint: 'video.text_footer_hint' as const },
];

/**
 * A ring that fills as the render proceeds.
 *
 * Determinate rather than a spinner: a render takes the better part of a
 * minute, and "something is happening" stops being reassuring long before it
 * finishes. The number is beside it as well, because a producer wants to know
 * whether to wait or go and do something else.
 */
function ProgressRing({ percent }: { percent: number }) {
  const clamped = Math.min(Math.max(percent, 0), 100);
  return (
    <span
      className="progress-ring"
      aria-hidden="true"
      style={{ background: `conic-gradient(currentColor ${clamped * 3.6}deg, transparent 0)` }}
    />
  );
}

interface Props {
  timeZone: string;
}

/**
 * Turning a clip into something postable.
 *
 * Radio is audio and every platform wants video, so this is where a producer
 * closes that gap. Audio comes either from something already transcribed or
 * from a file dropped straight onto this page; the words are corrected; a
 * picture and the wording are chosen; and the result plays here before anyone
 * posts it.
 *
 * The order of the page is the order of the work, and the review step sits in
 * the middle of it deliberately -- there is no path from a transcript to a
 * render that does not pass through a person reading the words first.
 */
export function VideoScreen({ timeZone }: Props) {
  const [transcripts, setTranscripts] = useState<Transcript[] | null>(null);
  const [videos, setVideos] = useState<VideoRecord[] | null>(null);
  const [picked, setPicked] = useState<Transcript | null>(null);
  const [cueSet, setCueSet] = useState<CueSet | null>(null);
  const [cues, setCues] = useState<Cue[]>([]);
  const [dirty, setDirty] = useState(false);

  const [aspect, setAspect] = useState<VideoAspect>('vertical');
  const [cardSize, setCardSize] = useState<VideoCardSize>('default');
  const [texts, setTexts] = useState<CardTexts>(blankTexts);
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [backgroundKey, setBackgroundKey] = useState<string | null>(null);
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(null);
  const [footageKey, setFootageKey] = useState<string | null>(null);
  const [footageName, setFootageName] = useState<string | null>(null);
  const [animated, setAnimated] = useState(true);

  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] =
    useState<'audio' | 'image' | 'background' | 'footage' | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<VideoRecord | null>(null);
  const [rendering, setRendering] = useState<VideoRecord | null>(null);
  /** Which row is mid-cancel. Negative ids are transcripts, so one flag does both. */
  const [stopping, setStopping] = useState<number | null>(null);
  /** The transcription an upload is currently waiting on, so it can be stopped. */
  const [waitingOn, setWaitingOn] = useState<number | null>(null);

  const audioInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const backgroundInput = useRef<HTMLInputElement>(null);
  const footageInput = useRef<HTMLInputElement>(null);
  const cancelled = useCancelledOnUnmount();

  const load = useCallback(async (): Promise<void> => {
    const [list, made] = await Promise.all([
      api.transcripts().catch(() => [] as Transcript[]),
      api.videos().catch(() => [] as VideoRecord[]),
    ]);
    if (cancelled.current) return;
    // A transcript outlives the recording it was made from, so an older one can
    // be perfectly readable and have nothing left to render. Offering it means
    // a producer clicks the button and is told no.
    setTranscripts(list.filter(
      (r) => r.status === 'ready' && r.lines.length > 0 && r.audio_available,
    ));
    setVideos(made);
  }, [cancelled]);

  useEffect(() => { void load(); }, [load]);

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      const known: Record<string, string> = {
        cues_not_reviewed: t('video.not_reviewed'),
        no_cues: t('video.no_cues'),
        too_long: t('video.too_long'),
        transcript_not_ready: t('video.transcript_not_ready'),
        audio_gone: t('video.audio_gone'),
        unsupported_type: t('transcribe.unsupported'),
        file_too_large: t('transcribe.too_large'),
        transcription_unavailable: t('transcript.unavailable'),
      };
      return known[err.code] ?? err.message;
    }
    return t('video.render_failed');
  }

  const pick = useCallback(async (transcript: Transcript): Promise<void> => {
    setPicked(transcript);
    setError(null);
    setCueSet(null);
    setTexts((prev) => ({ ...prev, title: transcript.source_name ?? prev.title }));
    try {
      const set = await api.cues(transcript.id);
      if (cancelled.current) return;
      setCueSet(set);
      setCues(set.cues);
      setDirty(!set.edited);
      // Filled in rather than applied invisibly, so a producer can see the
      // station line and the timestamp, keep them, change them, or clear them.
      setTexts((prev) => ({
        ...prev,
        station: set.suggested_texts.station,
        stamp: set.suggested_texts.stamp,
      }));
    } catch {
      setError(t('video.cues_failed'));
    }
  }, [cancelled]);

  /** Drop a recording here and the words for it appear without a detour. */
  async function uploadAudio(file: File): Promise<void> {
    setUploading('audio');
    setUploadPercent(0);
    setError(null);
    try {
      let transcript = await api.transcribeUpload(file, (fraction) => {
        setUploadPercent(Math.round(fraction * UPLOAD_SHARE));
      });
      setUploadPercent(UPLOAD_SHARE);
      // Held so the button below has something to stop. The bytes are already
      // sent by this point; what can still be called off is the decoding.
      setWaitingOn(transcript.id);

      // The transcription half has no progress to read, so it is estimated
      // against the clip's own length. The estimate creeps and stops short;
      // only the transcript coming back ready puts the bar on 100.
      const startedAt = Date.now();
      const expectedMs = (transcript.expected_sec ?? 0) > 0
        ? transcript.expected_sec! * 1000
        : TRANSCRIBE_OVERHEAD_MS + ((transcript.duration_sec ?? 0) / TRANSCRIBE_RATE) * 1000;

      const deadline = Date.now() + giveUpAfterMs(transcript.expected_sec, GIVE_UP_FLOOR_MS);
      while (
        (transcript.status === 'queued' || transcript.status === 'processing')
        && Date.now() < deadline && !cancelled.current
      ) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        transcript = await api.transcript(transcript.id);

        const share = Math.min((Date.now() - startedAt) / Math.max(expectedMs, 1), 1);
        // Rounded like the upload half. Without this the estimate reaches the
        // screen as 47.455382303212474%, which is not a thing to show anyone.
        setUploadPercent((was) => Math.max(
          was,
          Math.round(Math.min(UPLOAD_SHARE + (100 - UPLOAD_SHARE) * share, ESTIMATE_CEILING)),
        ));
      }
      if (cancelled.current) return;
      setUploadPercent(100);

      await load();
      if (transcript.status === 'cancelled') {
        setError(t('transcript.cancelled'));
      } else if (transcript.status === 'ready' && transcript.lines.length > 0) {
        await pick(transcript);
      } else if (transcript.status === 'ready') {
        setError(t('transcript.empty'));
      } else {
        setError(t('transcript.failed'));
      }
    } catch (err) {
      setError(describe(err));
    } finally {
      setUploading(null);
      setUploadPercent(0);
      setWaitingOn(null);
      if (audioInput.current) audioInput.current.value = '';
    }
  }

  /**
   * Both pictures go through the same endpoint and differ only in where the key
   * is kept: one becomes the avatar in the card, the other fills the frame
   * behind it.
   */
  async function uploadPicture(file: File, slot: 'image' | 'background'): Promise<void> {
    setUploading(slot);
    setError(null);
    try {
      const { image_key } = await api.uploadVideoImage(file);
      if (cancelled.current) return;
      const preview = URL.createObjectURL(file);
      if (slot === 'image') {
        setImageKey(image_key);
        setImagePreview(preview);
      } else {
        setBackgroundKey(image_key);
        setBackgroundPreview(preview);
      }
    } catch (err) {
      setError(describe(err));
    } finally {
      setUploading(null);
      const input = slot === 'image' ? imageInput.current : backgroundInput.current;
      if (input) input.value = '';
    }
  }

  async function uploadFootage(file: File): Promise<void> {
    setUploading('footage');
    setError(null);
    try {
      const { background_video_key } = await api.uploadBackgroundVideo(file);
      if (cancelled.current) return;
      setFootageKey(background_video_key);
      setFootageName(file.name);
    } catch (err) {
      setError(describe(err));
    } finally {
      setUploading(null);
      if (footageInput.current) footageInput.current.value = '';
    }
  }

  async function saveCues(): Promise<void> {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const set = await api.saveCues(picked.id, cues);
      if (cancelled.current) return;
      setCueSet(set);
      setCues(set.cues);
      setDirty(false);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function render(): Promise<void> {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createVideo({
        transcript_id: picked.id,
        aspect,
        title: texts.title.trim() === '' ? null : texts.title.trim(),
        texts,
        image_key: imageKey,
        background_key: backgroundKey,
        background_video_key: footageKey,
        animated,
        card_size: cardSize,
      });
      setVideos((prev) => [created, ...(prev ?? [])]);
      setRendering(created);

      // A render reports real progress, so the wait is measured against that
      // rather than against a clock: as long as the bar is still moving the
      // work is alive and giving up on it would be throwing away a finished
      // video. Only a render that has gone quiet for STALLED_MS is abandoned.
      // The fixed deadline this replaces was set on a laptop and was shorter
      // than a render takes on the server.
      let current = created;
      let lastProgress = current.progress;
      let lastStatus = current.status;
      let movedAt = Date.now();
      while (
        (current.status === 'queued' || current.status === 'rendering')
        && Date.now() - movedAt
          < (current.status === 'queued' ? QUEUE_PATIENCE_MS : STALLED_MS)
        && !cancelled.current
      ) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        current = await api.video(current.id);
        if (cancelled.current) return;
        // Reaching the front of the queue is movement too, and it is the only
        // movement there is between being accepted and the first frame drawn.
        if (current.progress !== lastProgress || current.status !== lastStatus) {
          lastProgress = current.progress;
          lastStatus = current.status;
          movedAt = Date.now();
        }
        setVideos((prev) => (prev ?? []).map((v) => (v.id === current.id ? current : v)));
        setRendering(current);
      }
      if (current.status === 'cancelled') {
        // Not an error: they asked for this. Said plainly so the button that
        // vanishes has an answer next to where it was.
        setError(t('video.cancelled'));
      } else if (current.status === 'failed') {
        setError(current.error_message ?? t('video.render_failed'));
      } else if (current.status === 'ready') {
        // Straight into the player: the point of making it is to look at it.
        setPlaying(current);
      }
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
      setRendering(null);
    }
  }

  /**
   * Stops work that is already running.
   *
   * The button exists because the wait is minutes long and a producer usually
   * knows within seconds that it is going to be wrong -- the wrong clip, a typo
   * they have just seen. Without it they either watch something they do not
   * want being made, or reload the page and leave the server making it anyway.
   *
   * The row is not removed from the list. A cancelled render is a thing that
   * happened, and a row that vanishes reads as a bug rather than as an answer.
   */
  async function stopVideo(id: number): Promise<void> {
    setStopping(id);
    try {
      const stopped = await api.cancelVideo(id);
      setVideos((prev) => (prev ?? []).map((v) => (v.id === stopped.id ? stopped : v)));
      if (rendering?.id === stopped.id) setRendering(stopped);
    } catch (err) {
      setError(describe(err));
    } finally {
      setStopping(null);
    }
  }

  /** The same, for the transcription half of an upload. */
  async function stopTranscript(id: number): Promise<void> {
    setStopping(-id);
    try {
      await api.cancelTranscript(id);
    } catch (err) {
      setError(describe(err));
    } finally {
      setStopping(null);
    }
  }

  const when = new Intl.DateTimeFormat('en-GB', {
    timeZone, hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit',
  });

  const reviewed = cueSet?.edited === true && !dirty;

  return (
    <section className="video-screen">
      <h2 className="screen-title">{t('video.title')}</h2>
      <p className="screen-lede">{t('video.lede')}</p>

      {error && <div className="banner error" role="alert">{error}</div>}

      {/* 1 — where the audio comes from */}
      <h3 className="section-title">{t('video.step_pick')}</h3>

      <div
        className={`dropzone${dragging ? ' over' : ''}${uploading === 'audio' ? ' busy' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file && uploading === null) void uploadAudio(file);
        }}
      >
        <span className="dropzone-title">{t('video.upload_audio')}</span>
        <span className="dropzone-hint">{t('video.upload_audio_hint')}</span>
        <button
          type="button"
          className="primary"
          disabled={uploading !== null}
          onClick={() => audioInput.current?.click()}
        >
          {uploading === 'audio' ? t('video.transcribing') : t('transcribe.choose_file')}
        </button>

        {uploading === 'audio' && (
          <div className="upload-progress" role="status" aria-live="polite">
            <div
              className="bar"
              role="progressbar"
              aria-valuenow={uploadPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span style={{ width: `${uploadPercent}%` }} />
            </div>
            <span className="mono">{`${uploadPercent}%`}</span>
            {waitingOn !== null && (
              <button
                type="button"
                className="stop"
                title={t('transcript.cancel_title')}
                aria-label={t('transcript.cancel_title')}
                disabled={stopping === -waitingOn}
                onClick={() => void stopTranscript(waitingOn)}
              >
                ✕
              </button>
            )}
            <span className="phase">
              {uploadPercent < UPLOAD_SHARE
                ? t('video.upload_sending')
                : t('video.upload_transcribing')}
            </span>
          </div>
        )}
        <input
          ref={audioInput}
          type="file"
          hidden
          accept="audio/*,video/mp4,video/quicktime,video/webm"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void uploadAudio(file);
          }}
        />
      </div>

      <p className="or-line">{t('video.or_existing')}</p>

      {transcripts === null && <div className="empty">{t('common.loading')}</div>}
      {transcripts?.length === 0 && <div className="empty">{t('video.no_transcripts')}</div>}
      {transcripts && transcripts.length > 0 && (
        <ul className="picker-list boxed">
          {transcripts.map((row) => (
            <li key={row.id} className={picked?.id === row.id ? 'selected' : ''}>
              <span className="picker-name">
                {row.source_name ?? when.format(new Date(row.created_at))}
              </span>
              <span className="picker-meta mono">
                {row.duration_sec !== null && formatDuration(row.duration_sec)}
                {` · ${row.lines.length}`}
              </span>
              <button type="button" onClick={() => void pick(row)}>
                {picked?.id === row.id ? t('video.picked') : t('video.pick')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* 2 — read it before anyone else does */}
      {picked && (
        <>
          <h3 className="section-title">{t('video.step_review')}</h3>
          <p className="review-note">{t('video.review_note')}</p>

          {cueSet === null && <div className="empty">{t('common.loading')}</div>}
          {cueSet && (
            <>
              <CueEditor
                cues={cues}
                problems={cueSet.problems}
                disabled={busy}
                onChange={(next) => { setCues(next); setDirty(true); }}
              />
              <div className="cue-save">
                <button
                  type="button"
                  className="primary"
                  disabled={busy || cues.length === 0}
                  onClick={() => void saveCues()}
                >
                  {reviewed ? t('video.reviewed') : t('video.confirm_review')}
                </button>
                {dirty && <span className="hint">{t('video.unsaved')}</span>}
              </div>
            </>
          )}
        </>
      )}

      {/* 3 — how it should look */}
      {picked && reviewed && (
        <>
          <h3 className="section-title">{t('video.step_design')}</h3>

          <div className="design-grid">
            <div className="design-block">
              <span className="field-label">{t('video.shape')}</span>
              <div className="aspect-choice">
                {ASPECTS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={aspect === option.value ? 'selected' : ''}
                    onClick={() => setAspect(option.value)}
                  >
                    <span className={`aspect-shape ${option.value}`} aria-hidden="true" />
                    <span className="aspect-label">{t(option.label)}</span>
                    <span className="aspect-note">{t(option.note)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="design-block">
              <span className="field-label">{t('video.card_size')}</span>
              <div className="aspect-choice">
                {CARD_SIZES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={cardSize === option.value ? 'selected' : ''}
                    onClick={() => setCardSize(option.value)}
                  >
                    <span className={`size-shape ${aspect}`} aria-hidden="true">
                      <span className={`size-card ${option.value}`} />
                    </span>
                    <span className="aspect-label">{t(option.label)}</span>
                    <span className="aspect-note">{t(option.note)}</span>
                  </button>
                ))}
              </div>
              <p className="hint">{t('video.card_size_hint')}</p>
            </div>

            <div className="design-block">
              <span className="field-label">{t('video.motion')}</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={animated}
                  onChange={(e) => setAnimated(e.target.checked)}
                />
                <span>{t('video.animated')}</span>
              </label>
              <p className="hint">{t('video.motion_hint')}</p>
            </div>

            <div className="design-block">
              <span className="field-label">{t('video.picture')}</span>
              <div className="image-choice">
                <div className="image-circle">
                  {imagePreview
                    ? <img src={imagePreview} alt="" />
                    : <span className="image-placeholder">ASHAMS</span>}
                </div>
                <div className="image-actions">
                  <button
                    type="button"
                    disabled={uploading !== null}
                    onClick={() => imageInput.current?.click()}
                  >
                    {uploading === 'image' ? t('video.uploading') : t('video.choose_picture')}
                  </button>
                  {imageKey && (
                    <button
                      type="button"
                      onClick={() => { setImageKey(null); setImagePreview(null); }}
                    >
                      {t('video.remove_picture')}
                    </button>
                  )}
                  <span className="hint">{t('video.picture_hint')}</span>
                </div>
                <input
                  ref={imageInput}
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void uploadPicture(file, 'image');
                  }}
                />
              </div>
            </div>
          </div>

          <div className="design-block">
            <span className="field-label">{t('video.background')}</span>
            <div className="image-choice">
              <div className="background-thumb">
                {backgroundPreview
                  ? <img src={backgroundPreview} alt="" />
                  : <span className="image-placeholder">{t('video.background_none')}</span>}
              </div>
              <div className="image-actions">
                <button
                  type="button"
                  disabled={uploading !== null}
                  onClick={() => backgroundInput.current?.click()}
                >
                  {uploading === 'background'
                    ? t('video.uploading')
                    : t('video.choose_background')}
                </button>
                {backgroundKey && (
                  <button
                    type="button"
                    onClick={() => { setBackgroundKey(null); setBackgroundPreview(null); }}
                  >
                    {t('video.remove_picture')}
                  </button>
                )}
                <span className="hint">{t('video.background_hint')}</span>
              </div>
              <input
                ref={backgroundInput}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadPicture(file, 'background');
                }}
              />
            </div>
          </div>

          <div className="design-block">
            <span className="field-label">{t('video.footage')}</span>
            <div className="image-actions">
              <button
                type="button"
                disabled={uploading !== null}
                onClick={() => footageInput.current?.click()}
              >
                {uploading === 'footage' ? t('video.uploading') : t('video.choose_footage')}
              </button>
              {footageKey && (
                <>
                  <span className="hint mono">{footageName}</span>
                  <button
                    type="button"
                    onClick={() => { setFootageKey(null); setFootageName(null); }}
                  >
                    {t('video.remove_picture')}
                  </button>
                </>
              )}
              <span className="hint">
                {footageKey && backgroundKey
                  ? t('video.footage_with_image')
                  : t('video.footage_hint')}
              </span>
            </div>
            <input
              ref={footageInput}
              type="file"
              hidden
              accept="video/mp4,video/quicktime,video/webm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadFootage(file);
              }}
            />
          </div>

          <span className="field-label">{t('video.on_screen_text')}</span>
          <div className="texts-grid">
            {TEXT_FIELDS.map((field) => (
              <label key={field.key} className="field">
                <span>{t(field.label)}</span>
                <input
                  type="text"
                  maxLength={160}
                  value={texts[field.key]}
                  placeholder={t(field.hint)}
                  onChange={(e) =>
                    setTexts((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              </label>
            ))}
          </div>

          <div className="render-row">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void render()}
            >
              {busy && (
                <ProgressRing percent={rendering?.progress ?? 0} />
              )}
              {busy ? t('video.rendering') : t('video.render')}
            </button>
            {busy && (
              <span
                className="hint mono"
                role="status"
                aria-live="polite"
              >
                {`${rendering?.progress ?? 0}%`}
              </span>
            )}
            {busy && rendering && (
              <button
                type="button"
                className="stop"
                title={t('video.cancel_title')}
                aria-label={t('video.cancel_title')}
                disabled={stopping === rendering.id}
                onClick={() => void stopVideo(rendering.id)}
              >
                ✕
              </button>
            )}
            {busy && rendering?.status === 'queued' && (
              <span className="hint">
                {(rendering.queued_ahead ?? 0) > 0
                  ? t('video.queued_behind', { n: rendering.queued_ahead ?? 0 })
                  : t('video.queued_next')}
              </span>
            )}
            {busy && rendering?.status !== 'queued'
              && <span className="hint">{t('video.render_wait')}</span>}
            {/* The banner at the top of the page is nowhere near this button.
                A refusal has to appear where the person is looking. */}
            {!busy && error && <span className="inline-error">{error}</span>}
          </div>
        </>
      )}

      {/* 4 — what has been made, and watching it */}
      <h3 className="section-title">{t('video.made')}</h3>
      {/* Said where the videos are, because they do not stay (D84). */}
      <p className="page-note">{t('video.cleared_daily')}</p>
      {videos?.length === 0 && <div className="empty">{t('video.none_yet')}</div>}
      <ul className="video-list">
        {(videos ?? []).map((video) => (
          <li key={video.id} className="video-row">
            <span className={`pill ${video.status}`}>{t(`video.status.${video.status}`)}</span>
            <span className="video-name">
              {video.title ?? when.format(new Date(video.created_at))}
            </span>
            <span className="video-meta mono">
              {video.width}×{video.height}
              {video.duration_sec !== null && ` · ${formatDuration(video.duration_sec)}`}
              {video.file_size_bytes !== null && ` · ${formatBytes(video.file_size_bytes)}`}
            </span>
            {(video.status === 'queued' || video.status === 'rendering') && (
              <span className="row-progress">
                <span className="bar"><span style={{ width: `${video.progress}%` }} /></span>
                <span className="mono">
                  {video.status === 'queued' && (video.queued_ahead ?? 0) > 0
                    ? t('video.queued_behind', { n: video.queued_ahead ?? 0 })
                    : `${video.progress}%`}
                </span>
                <button
                  type="button"
                  className="stop"
                  title={t('video.cancel_title')}
                  aria-label={t('video.cancel_title')}
                  disabled={stopping === video.id}
                  onClick={() => void stopVideo(video.id)}
                >
                  ✕
                </button>
              </span>
            )}
            {video.stream_url
              ? (
                <span className="video-actions">
                  <button type="button" onClick={() => setPlaying(video)}>
                    {t('video.watch')}
                  </button>
                  <a className="download-link" href={video.download_url ?? ''} download>
                    {t('video.download')}
                  </a>
                </span>
                )
              : <span className="hint">{video.error_message ?? ''}</span>}
          </li>
        ))}
      </ul>

      {/* Watching it before posting it. */}
      {playing?.stream_url && (
        <div
          className="video-modal"
          role="dialog"
          aria-label={t('video.watch')}
          onClick={() => setPlaying(null)}
        >
          <div
            className={`video-frame ${playing.aspect}`}
            onClick={(e) => e.stopPropagation()}
          >
            <video src={playing.stream_url} controls autoPlay playsInline />
            <div className="video-modal-foot">
              <a className="download-link" href={playing.download_url ?? ''} download>
                {t('video.download')}
              </a>
              <button type="button" onClick={() => setPlaying(null)}>
                {t('help.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
